import { z } from 'zod';
import { auditFieldsSchema, centsSchema, idSchema, isoDateSchema, paginated, paginationQuerySchema, } from './common.js';
/* ============================= Unidade de medida ========================= */
export const MEASURE_UNITS = [
    'UN', 'KG', 'G', 'L', 'ML', 'M', 'M2', 'M3', 'SC', 'CX', 'PC', 'PAR', 'RL', 'MI', 'H',
];
export const measureUnitSchema = z.enum(MEASURE_UNITS);
export const MEASURE_UNIT_LABELS = {
    UN: 'Unidade',
    KG: 'Quilograma',
    G: 'Grama',
    L: 'Litro',
    ML: 'Mililitro',
    M: 'Metro',
    M2: 'Metro quadrado',
    M3: 'Metro cúbico',
    SC: 'Saco',
    CX: 'Caixa',
    PC: 'Peça',
    PAR: 'Par',
    RL: 'Rolo',
    MI: 'Milheiro',
    H: 'Hora',
};
/** Quantidade em milésimos: inteiro, positivo. */
export const qtyMilliSchema = z.number().int('Quantidade inválida');
export const positiveQtyMilliSchema = qtyMilliSchema.positive('Informe uma quantidade maior que zero');
/* =================================== Itens =============================== */
export const itemSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    sku: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    unit: measureUnitSchema,
    categoryId: idSchema.nullable(),
    categoryName: z.string().nullable(),
    avgCostCents: z.number().int(),
    lastCostCents: z.number().int(),
    minQtyMilli: z.number().int(),
    maxQtyMilli: z.number().int().nullable(),
    ncm: z.string().nullable(),
    barcode: z.string().nullable(),
    /// Cloro, alimento, remédio: o que vence e precisa ser rastreado até o lote.
    tracksBatch: z.boolean(),
    isActive: z.boolean(),
    /// Somatório dos saldos em todos os locais.
    totalQtyMilli: z.number().int(),
    /// true quando o saldo total caiu abaixo do mínimo — alimenta a AttentionStrip.
    belowMinimum: z.boolean(),
});
export const itemInputSchema = z.object({
    sku: z
        .string()
        .trim()
        .min(1, 'Informe o código do item')
        .max(40)
        .regex(/^[0-9A-Za-z.\-_]+$/, 'Use letras, números, ponto, hífen ou sublinhado')
        .toUpperCase(),
    name: z.string().trim().min(2, 'Informe o nome do item').max(180),
    description: z.string().trim().max(1000).optional().nullable(),
    unit: measureUnitSchema.default('UN'),
    categoryId: idSchema.optional().nullable(),
    minQtyMilli: qtyMilliSchema.min(0).default(0),
    maxQtyMilli: qtyMilliSchema.min(0).optional().nullable(),
    ncm: z.string().trim().max(10).optional().nullable(),
    barcode: z.string().trim().max(40).optional().nullable(),
    tracksBatch: z.boolean().default(false),
    isActive: z.boolean().default(true),
});
export const itemListQuerySchema = paginationQuerySchema.extend({
    categoryId: idSchema.optional(),
    unit: measureUnitSchema.optional(),
    belowMinimum: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    isActive: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const itemListResponseSchema = paginated(itemSchema);
/* ============================ Locais de estoque ========================== */
export const STOCK_LOCATION_KINDS = ['ALMOXARIFADO', 'FRENTE_OBRA', 'CANTEIRO', 'TERCEIRO'];
export const stockLocationKindSchema = z.enum(STOCK_LOCATION_KINDS);
export const STOCK_LOCATION_KIND_LABELS = {
    ALMOXARIFADO: 'Almoxarifado',
    FRENTE_OBRA: 'Frente de obra',
    CANTEIRO: 'Canteiro',
    TERCEIRO: 'Em poder de terceiro',
};
export const stockLocationSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    name: z.string(),
    kind: stockLocationKindSchema,
    unitId: idSchema.nullable(),
    isActive: z.boolean(),
    itemCount: z.number().int(),
    totalValueCents: z.number().int(),
});
export const stockLocationInputSchema = z.object({
    code: z.string().trim().min(1, 'Informe o código').max(20).toUpperCase(),
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    kind: stockLocationKindSchema.default('ALMOXARIFADO'),
    unitId: idSchema.optional().nullable(),
    isActive: z.boolean().default(true),
});
/* ================================= Saldos ================================ */
export const stockBalanceSchema = z.object({
    itemId: idSchema,
    sku: z.string(),
    itemName: z.string(),
    unit: measureUnitSchema,
    locationId: idSchema,
    locationName: z.string(),
    quantityMilli: z.number().int(),
    avgCostCents: z.number().int(),
    totalValueCents: z.number().int(),
    minQtyMilli: z.number().int(),
    belowMinimum: z.boolean(),
});
export const stockBalanceQuerySchema = paginationQuerySchema.extend({
    locationId: idSchema.optional(),
    itemId: idSchema.optional(),
    belowMinimum: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    /// Esconde as linhas zeradas — o padrão do dia a dia do almoxarife.
    hideZero: z
        .enum(['true', 'false'])
        .default('true')
        .transform((v) => v === 'true'),
});
export const stockBalanceListResponseSchema = paginated(stockBalanceSchema);
/* ============================ Lote e validade ============================ */
/**
 * O lote é a embalagem que chegou: código do fabricante e validade.
 *
 * Só item marcado como `tracksBatch` exige lote. O resto do estoque —
 * cimento, cano, parafuso — continua se movimentando sem nada disso.
 */
export const stockBatchInputSchema = z.object({
    /// Como está escrito na embalagem. Maiúsculas para "l2026a" e "L2026A" não
    /// virarem dois lotes do mesmo material.
    code: z.string().trim().min(1, 'Informe o lote').max(60).toUpperCase(),
    /// Dia civil (aaaa-mm-dd). Opcional: há lote rastreado que não vence.
    ///
    /// `isoDateSchema` valida o CALENDÁRIO, não só o formato: "2026-02-31"
    /// passaria num regex e o `new Date()` do serviço o empurraria para 3 de
    /// março — com o alerta de vencimento e a ordem de saída errados junto.
    expiryDate: isoDateSchema.optional().nullable(),
});
/** Situação do lote na prateleira — derivada da data, nunca guardada. */
export const BATCH_SITUATIONS = ['VENCIDO', 'VENCE_EM_BREVE', 'NO_PRAZO', 'SEM_VALIDADE'];
export const batchSituationSchema = z.enum(BATCH_SITUATIONS);
export const BATCH_SITUATION_LABELS = {
    VENCIDO: 'Vencido',
    VENCE_EM_BREVE: 'Vence em breve',
    NO_PRAZO: 'No prazo',
    SEM_VALIDADE: 'Sem validade',
};
/** Janela do "vence em breve". 30 dias é o prazo de recompra do almoxarifado. */
export const BATCH_EXPIRY_WARNING_DAYS = 30;
export const stockBatchSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    itemId: idSchema,
    sku: z.string(),
    itemName: z.string(),
    unit: measureUnitSchema,
    locationId: idSchema,
    locationName: z.string(),
    code: z.string(),
    expiryDate: z.string().nullable(),
    quantityMilli: z.number().int(),
    /// Quanto vale o que está parado neste lote, pelo médio do local.
    totalCostCents: z.number().int(),
    situation: batchSituationSchema,
    /// Negativo quando já venceu. Nulo quando o lote não tem validade.
    daysToExpiry: z.number().int().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const stockBatchListQuerySchema = paginationQuerySchema.extend({
    itemId: idSchema.optional(),
    locationId: idSchema.optional(),
    situation: batchSituationSchema.optional(),
    /// Esconde lote zerado — o padrão de quem está olhando a prateleira.
    hideZero: z
        .enum(['true', 'false'])
        .default('true')
        .transform((v) => v === 'true'),
});
export const stockBatchListResponseSchema = paginated(stockBatchSchema);
/** Quanto saiu de cada lote numa movimentação — o rastro do que foi embora. */
export const stockMovementBatchSchema = z.object({
    batchId: idSchema,
    code: z.string(),
    expiryDate: z.string().nullable(),
    quantityMilli: z.number().int(),
});
/**
 * Quantos dias faltam para o lote vencer, contados em dia civil.
 *
 * Função pura e compartilhada de propósito: a API decide a situação e o painel
 * pinta a etiqueta. Duas contas diferentes dariam telas que discordam.
 */
export function daysToExpiry(expiryDate, today) {
    if (!expiryDate)
        return null;
    const fim = Date.parse(`${expiryDate.slice(0, 10)}T00:00:00.000Z`);
    const hoje = Date.parse(`${today.slice(0, 10)}T00:00:00.000Z`);
    if (Number.isNaN(fim) || Number.isNaN(hoje))
        return null;
    return Math.round((fim - hoje) / 86_400_000);
}
/** A situação do lote a partir dos dias que faltam. */
export function batchSituation(dias) {
    if (dias === null)
        return 'SEM_VALIDADE';
    if (dias < 0)
        return 'VENCIDO';
    if (dias <= BATCH_EXPIRY_WARNING_DAYS)
        return 'VENCE_EM_BREVE';
    return 'NO_PRAZO';
}
/* ============================= Movimentações ============================= */
export const STOCK_MOVEMENT_TYPES = ['ENTRADA', 'SAIDA', 'TRANSFERENCIA', 'AJUSTE'];
export const stockMovementTypeSchema = z.enum(STOCK_MOVEMENT_TYPES);
export const STOCK_MOVEMENT_TYPE_LABELS = {
    ENTRADA: 'Entrada',
    SAIDA: 'Saída',
    TRANSFERENCIA: 'Transferência',
    AJUSTE: 'Ajuste',
};
export const STOCK_MOVEMENT_ORIGINS = [
    'MANUAL',
    'RECEBIMENTO',
    'DOCUMENTO_FISCAL',
    'INVENTARIO',
    'TRANSFERENCIA',
];
export const stockMovementOriginSchema = z.enum(STOCK_MOVEMENT_ORIGINS);
export const STOCK_MOVEMENT_ORIGIN_LABELS = {
    MANUAL: 'Lançamento manual',
    RECEBIMENTO: 'Recebimento de compra',
    DOCUMENTO_FISCAL: 'Documento fiscal',
    INVENTARIO: 'Inventário',
    TRANSFERENCIA: 'Transferência',
};
export const stockMovementSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    itemId: idSchema,
    sku: z.string(),
    itemName: z.string(),
    unit: measureUnitSchema,
    locationId: idSchema,
    locationName: z.string(),
    type: stockMovementTypeSchema,
    origin: stockMovementOriginSchema,
    originId: idSchema.nullable(),
    quantityMilli: z.number().int(),
    unitCostCents: z.number().int(),
    totalCostCents: z.number().int(),
    costCenterId: idSchema.nullable(),
    costCenterName: z.string().nullable(),
    balanceBeforeMilli: z.number().int(),
    balanceAfterMilli: z.number().int(),
    reason: z.string().nullable(),
    pairId: idSchema.nullable(),
    /// Estorno: contra-movimento apontando para o original.
    isReversal: z.boolean(),
    reversesId: idSchema.nullable(),
    movedAt: z.string(),
    createdAt: z.string(),
    createdById: idSchema.nullable(),
    /// De quais lotes saiu, ou em qual entrou. Vazio para item sem controle.
    batches: z.array(z.object({
        batchId: idSchema,
        code: z.string(),
        expiryDate: z.string().nullable(),
        quantityMilli: z.number().int(),
    })),
});
/**
 * Data do movimento informada pelo operador. Data inválida viraria `Invalid
 * Date` e erro 500 no banco; data futura mentiria no livro de estoque.
 */
const movedAtInputSchema = z
    .string()
    .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Data do movimento inválida' })
    .refine((v) => Date.parse(v) <= Date.now() + 24 * 60 * 60 * 1000, {
    message: 'A data do movimento não pode estar no futuro',
});
/** Entrada avulsa (sem pedido de compra). Exige custo para o médio não zerar. */
export const stockEntryInputSchema = z.object({
    itemId: idSchema,
    locationId: idSchema,
    quantityMilli: positiveQtyMilliSchema,
    unitCostCents: centsSchema.min(0),
    /// Obrigatório quando o item controla lote; recusado quando não controla.
    /// A checagem é na API, que é quem sabe como o item está cadastrado.
    batch: stockBatchInputSchema.optional().nullable(),
    reason: z.string().trim().max(300).optional().nullable(),
    movedAt: movedAtInputSchema.optional(),
});
/**
 * Saída para a obra. O centro de custo é OBRIGATÓRIO: é o que transforma
 * "saiu material do almoxarifado" em "esta etapa consumiu tanto".
 */
export const stockIssueInputSchema = z.object({
    itemId: idSchema,
    locationId: idSchema,
    quantityMilli: positiveQtyMilliSchema,
    costCenterId: idSchema,
    /// Baixa de lote VENCIDO. Por padrão a saída pula o que venceu e a API
    /// recusa quando só sobra vencido: material vencido não vai para a obra
    /// por engano. Descarte é uma decisão, e uma decisão se marca.
    allowExpired: z.boolean().default(false),
    reason: z.string().trim().max(300).optional().nullable(),
    movedAt: movedAtInputSchema.optional(),
});
export const stockTransferInputSchema = z
    .object({
    itemId: idSchema,
    fromLocationId: idSchema,
    toLocationId: idSchema,
    quantityMilli: positiveQtyMilliSchema,
    reason: z.string().trim().max(300).optional().nullable(),
    movedAt: movedAtInputSchema.optional(),
})
    .refine((v) => v.fromLocationId !== v.toLocationId, {
    message: 'A origem e o destino precisam ser locais diferentes',
    path: ['toLocationId'],
});
/** Ajuste manual: sempre com justificativa, porque mexe no saldo sem lastro. */
export const stockAdjustInputSchema = z.object({
    itemId: idSchema,
    locationId: idSchema,
    /// Quantidade final desejada, não a diferença — é como o almoxarife conta.
    countedQtyMilli: qtyMilliSchema.min(0),
    /// Só para SOBRA em item com lote: material que apareceu tem de dizer de
    /// qual lote é. Falta sai pelo mesmo critério da saída, começando pelo que
    /// vence primeiro — inclusive o já vencido, que some da prateleira também.
    batch: stockBatchInputSchema.optional().nullable(),
    reason: z.string().trim().min(5, 'Justifique o ajuste').max(300),
    movedAt: movedAtInputSchema.optional(),
});
export const stockMovementListQuerySchema = paginationQuerySchema.extend({
    itemId: idSchema.optional(),
    locationId: idSchema.optional(),
    costCenterId: idSchema.optional(),
    type: stockMovementTypeSchema.optional(),
    origin: stockMovementOriginSchema.optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const stockMovementListResponseSchema = paginated(stockMovementSchema);
/* ============================== Inventário =============================== */
export const STOCK_COUNT_STATUSES = ['ABERTO', 'FECHADO', 'CANCELADO'];
export const stockCountStatusSchema = z.enum(STOCK_COUNT_STATUSES);
export const stockCountItemSchema = z.object({
    id: idSchema,
    itemId: idSchema,
    sku: z.string(),
    itemName: z.string(),
    unit: measureUnitSchema,
    expectedQtyMilli: z.number().int(),
    countedQtyMilli: z.number().int().nullable(),
    differenceQtyMilli: z.number().int().nullable(),
    notes: z.string().nullable(),
    /// Item que exige lote: a SOBRA encontrada precisa dizer de qual lote é.
    tracksBatch: z.boolean(),
    batchCode: z.string().nullable(),
    batchExpiry: z.string().nullable(),
});
export const stockCountSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    code: z.string(),
    locationId: idSchema,
    locationName: z.string(),
    status: stockCountStatusSchema,
    notes: z.string().nullable(),
    startedAt: z.string(),
    closedAt: z.string().nullable(),
    items: z.array(stockCountItemSchema),
    countedCount: z.number().int(),
    divergenceCount: z.number().int(),
});
export const stockCountInputSchema = z.object({
    locationId: idSchema,
    notes: z.string().trim().max(500).optional().nullable(),
});
export const stockCountFillSchema = z.object({
    items: z
        .array(z.object({
        itemId: idSchema,
        countedQtyMilli: qtyMilliSchema.min(0),
        notes: z.string().trim().max(300).optional().nullable(),
        /// Lote da SOBRA, quando o item controla lote. Material que apareceu
        /// na contagem tem de dizer de onde veio; a falta sai pelo que vence
        /// primeiro e não precisa de nada.
        batchCode: z.string().trim().max(60).toUpperCase().optional().nullable(),
        batchExpiry: isoDateSchema.optional().nullable(),
    }))
        .min(1, 'Informe ao menos um item contado'),
});
export const stockCountCloseSchema = z.object({
    reason: z.string().trim().min(5, 'Justifique o fechamento do inventário').max(300),
});
/* ============================== Relatórios =============================== */
/** Consumo de material por centro de custo — o espelho físico do custo. */
export const consumptionReportQuerySchema = z.object({
    from: z.string(),
    to: z.string(),
    costCenterId: idSchema.optional(),
    groupBy: z.enum(['costCenter', 'item']).default('costCenter'),
});
export const consumptionReportRowSchema = z.object({
    key: z.string(),
    code: z.string().nullable(),
    label: z.string(),
    movementCount: z.number().int(),
    quantityMilli: z.number().int(),
    unit: measureUnitSchema.nullable(),
    totalCostCents: z.number().int(),
    sharePercent: z.number(),
});
export const consumptionReportResponseSchema = z.object({
    rows: z.array(consumptionReportRowSchema),
    totals: z.object({ totalCostCents: z.number().int(), movementCount: z.number().int() }),
});
//# sourceMappingURL=estoque.js.map